// <Tx> — ONE field, rendered in the operator's content language, with the provenance of what you
// are reading stated on the spot. CONTENT-TRANSLATION-ARCH §5.3.
//
// The rule this component exists to keep: A TRANSLATION NEVER MASQUERADES AS THE RECORD.
// When what you are reading is a projection, a mark says so, in ATTEST's own claim vocabulary —
// ◇ `att--proposed` for a machine translation (a proposal about wording), ▪ `att--held` for one an
// operator has stood behind. The original is always one click away, and the chip that takes you
// there is mono, because it names a language, and a language name is checkable identity (LAW 1).
//
// Costs nothing per field: the text comes from the page's single batched resolve (content.tsx).
// A field with no translation, no engagement in scope, or a resolve that failed renders the
// record verbatim with no adornment — the honest fallback in all three cases.
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { useT } from '../i18n/index.js';
import { useContentLang, useTx } from '../i18n/content.js';
import { LANGUAGE_LABEL, type Language } from '../../shared/language.js';
import { isLanguageFlag, type ContentEntityType } from '../../shared/contentTranslation.js';
import './Tx.de.js';
import './Tx.fr.js';

const label = (lang: string | null): string =>
  (lang && LANGUAGE_LABEL[lang as Language]) || (lang ?? '').toUpperCase();

export interface TxProps {
  type: ContentEntityType;
  /** The entity's id. Null/undefined (a row whose revision id never reached the client) renders
   *  the record with no adornment rather than guessing. */
  id: number | null | undefined;
  field: string;
  /** The RECORD — the canonical text, as the page already holds it. Always rendered when there is
   *  nothing else honest to show. */
  original: string | null | undefined;
  className?: string;
  style?: CSSProperties;
  /** Element to render the text in. Lists use a span or div; a statement uses <p>. */
  as?: 'span' | 'p' | 'div';
  /** Override the engagement — for a surface not scoped to the console's current one. */
  engagementId?: number | null;
  /** Show nothing at all when there is no text (instead of the fallback the caller passes). */
  empty?: ReactNode;
  /** How to render the text, for surfaces that do more than print a string — the workbench runs
   *  its prose through <Rich>. Given the text to render (translation or record) and told which
   *  it is, because a caller may want to dress a projection differently. */
  render?: (text: string, showing: 'original' | 'translation') => ReactNode;
}

/**
 * The mark that rides beside a field: the original-language chip (click to flip this one item)
 * and the claim mark for what you are reading.
 */
export function TxMark({ state, compact = false }: { state: ReturnType<typeof useTx>; compact?: boolean }) {
  const t = useT();
  const { showBadges } = useContentLang();
  const { showing, originalLang, detector, provenance, model, stale, flags, pending, frozen, lang } = state;

  const readingTranslation = showing === 'translation';
  const langFlag = (flags ?? []).find(isLanguageFlag) ?? null;
  const termFlags = (flags ?? []).filter((f) => !isLanguageFlag(f));
  // The chip appears whenever it is carrying information: you are reading a projection, or a
  // translation is on its way, or the operator has asked to see every item's language.
  const wantChip = readingTranslation || pending || (showBadges && !!originalLang);
  if (!wantChip || !originalLang) return null;

  const assumed = detector === 'default_correspondence';
  const chipTitle = readingTranslation
    ? t('Original: {language} — click to read it', { language: label(originalLang) })
    : pending
      ? t('Original: {language}. The {target} translation has not landed yet.', { language: label(originalLang), target: label(lang) })
      : t('Written in {language}{assumed} — click to read it in {target}', {
          language: label(originalLang),
          assumed: assumed ? t(' (assumed)') : '',
          target: label(lang),
        });

  const flip = () => {
    if (readingTranslation) state.setItemLang('original');
    else if (state.flipped) state.setItemLang(null);
    else if (lang) state.setItemLang(lang);
  };

  const claim = readingTranslation
    ? (provenance === 'operator'
        ? { cls: 'att--held', glyph: '▪', title: t('Translation corrected by an operator') }
        : { cls: 'att--proposed', glyph: '◇', title: t('Machine translation ({model}) — a proposal about wording, not the record', { model: model ?? '—' }) })
    : null;

  return (
    <span className={`tx-mark${compact ? ' tx-mark--compact' : ''}`}>
      <button type="button" className={`tx-chip src-ref${assumed ? ' tx-chip--assumed' : ''}`} title={chipTitle} onClick={flip}>
        {originalLang.toUpperCase()}
      </button>
      {claim ? <span className={`tx-claim ${claim.cls}`} title={claim.title} aria-label={claim.title}>{claim.glyph}</span> : null}
      {pending ? <span className="tx-chip tx-chip--pending" title={t('Translating…')}>…</span> : null}
      {stale ? (
        <span className="tx-chip tx-chip--attend" title={provenance === 'operator'
          ? t('The source has changed since this translation was written — review it')
          : t('The source or the terminology has changed — a fresh translation is on its way')}>!</span>
      ) : null}
      {frozen ? <span className="tx-chip tx-chip--frozen" title={t('A superseded version — its translation is kept as it was and is never refreshed')}>◦</span> : null}
      {langFlag ? (
        // Louder than a terminology miss, because it is a different KIND of wrong: the text is
        // not the language it is being offered as. Reading it as the translation would be
        // reading a claim that is simply false.
        <span className="tx-chip tx-chip--broken"
              title={t('This does not read as {expected} — it looks like {detected}. Shown anyway; the record is one click away.', {
                expected: label(langFlag.expected), detected: label(langFlag.detected),
              })}>⚠</span>
      ) : null}
      {termFlags.length ? (
        <span className="tx-chip tx-chip--attend" title={t('Terminology not carried through: {terms}', { terms: termFlags.map((f) => f.expected).join(', ') })}>◆</span>
      ) : null}
    </span>
  );
}

/**
 * The field itself. Renders the translation when there is one to render and the reader asked for
 * it; otherwise the record, verbatim.
 */
export function Tx({ type, id, field, original, className, style, as = 'span', engagementId, empty, render }: TxProps) {
  const state = useTx(type, id, field, engagementId);
  const text = state.showing === 'translation' ? state.text : (original ?? null);
  if (!text) return <>{empty ?? null}</>;
  const Tag = as;
  return (
    <>
      {render ? render(text, state.showing) : <Tag className={className} style={style}>{text}</Tag>}
      <TxMark state={state} compact={as !== 'span'} />
    </>
  );
}

/**
 * The operator's controls for one field — detail surfaces only, never a list. Correcting a
 * translation is a commit (P1's other half): it takes that row out of the machine's reach for
 * good, and it is audited. Reverting gives it back, and says so before it does.
 */
export function TxTools({ type, id, field, original, engagementId }: Omit<TxProps, 'className' | 'style' | 'as' | 'empty'>) {
  const t = useT();
  const state = useTx(type, id, field, engagementId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { if (editing) area.current?.focus(); }, [editing]);

  const open = useCallback(() => {
    setDraft(state.text ?? original ?? '');
    setError(null);
    setEditing(true);
  }, [state.text, original]);

  const save = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      await state.saveCorrection(draft.trim());
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(false); }
  }, [state, draft]);

  const revert = useCallback(async () => {
    if (state.provenance === 'operator'
      && !window.confirm(t('Reverting discards your wording and asks the model again. Continue?'))) return;
    setBusy(true); setError(null);
    try { await state.revertToMachine(); } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(false); }
  }, [state, t]);

  // Nothing to correct: the field is being read in the language it was written in.
  if (!state.lang || state.showing === 'original') return null;

  return (
    <div className="tx-tools">
      {editing ? (
        <>
          <textarea
            ref={area}
            className="form-input tx-tools-area"
            rows={4}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="tx-tools-verbs">
            <button type="button" className="btn btn-accent btn-sm" disabled={busy || !draft.trim()} onClick={() => void save()}>
              {t('Save the translation')}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setEditing(false)}>{t('Cancel')}</button>
          </div>
        </>
      ) : (
        <div className="tx-tools-verbs">
          <button type="button" className="lnk" onClick={open}>{t('Correct this translation')}</button>
          <button type="button" className="lnk" disabled={busy} onClick={() => void revert()}>
            {state.provenance === 'operator' ? t('Revert to the machine translation') : t('Translate this again')}
          </button>
        </div>
      )}
      {error ? <div className="tx-tools-err">{error}</div> : null}
    </div>
  );
}

/**
 * The topbar setting — which language engagement CONTENT is read in, beside the switch that
 * decides what the console's own labels say. Two questions, two controls; 'Follow the console'
 * is the value for an operator who wants them to move together.
 */
export function ContentLangControl() {
  const t = useT();
  const { pref, setPref, showBadges, setShowBadges } = useContentLang();
  return (
    <span className="tx-langset" role="group" aria-label={t('Content language')}>
      <select
        className="form-select tx-langsel"
        value={pref}
        title={t('The language engagement content is shown in. The record itself never changes.')}
        onChange={(e) => setPref(e.target.value as typeof pref)}
      >
        <option value="original">{t('Content: as written')}</option>
        <option value="ui">{t('Content: follow the console')}</option>
        <option value="de">{t('Content: {language}', { language: LANGUAGE_LABEL.de })}</option>
        <option value="en">{t('Content: {language}', { language: LANGUAGE_LABEL.en })}</option>
        <option value="fr">{t('Content: {language}', { language: LANGUAGE_LABEL.fr })}</option>
      </select>
      <button
        type="button"
        className={`tx-badgebtn${showBadges ? ' on' : ''}`}
        aria-pressed={showBadges}
        title={t('Show the original language on every item')}
        onClick={() => setShowBadges(!showBadges)}
      >AB</button>
    </span>
  );
}
